import { Suspense } from 'react';
import { Switch } from 'react-router-dom';

import * as Sentry from '@sentry/react';
import { SentryRoute, SuspenseFallback } from 'common/components';
import { lazyWithRetry } from 'common/utils';
import { AlertsContainer, ApiErrorAlertContainer } from 'modules/alerts/components';
import { ErrorHandler } from 'modules/app/components';
import { AppRoutes } from 'modules/router';
import { NattersRoutes } from 'natters/routes';
import { NatalieRoutes, RESERVED_NATALIE_SUBROUTES } from 'pages/Natalie';

import App from './App';
import { useIntercom, useInternetConnectionWatcher } from './hooks';

const INVITE_CODE_PATTERN = /^[A-Za-z0-9_-]{12}$/;

const lazyLoadedPages = {
  TechCheck: lazyWithRetry(() => import('pages/TechCheck'), 'TechCheck'),
  PublicReport: lazyWithRetry(() => import('pages/report/unified/public'), 'PublicReport'),
  PublicEventReport: lazyWithRetry(() => import('pages/report/event/public'), 'PublicEventReport'),
  EventAddToCalendar: lazyWithRetry(() => import('event/pages/EventAddToCalendar'), 'EventAddToCalendar'),
  EventPublic: lazyWithRetry(() => import('pages/PublicEventDetails'), 'PublicEventDetails'),
  CustomLogin: lazyWithRetry(() => import('pages/CustomLogin'), 'CustomLogin'),
  CheckYourInbox: lazyWithRetry(() => import('pages/MagicLink/CheckYourInbox'), 'CheckYourInbox'),
  LinkExpired: lazyWithRetry(() => import('pages/MagicLink/LinkExpired'), 'LinkExpired'),
  LinkInvalid: lazyWithRetry(() => import('pages/MagicLink/LinkInvalid'), 'LinkInvalid'),
  OpenNatterPublic: lazyWithRetry(() => import('natters/pages/OpenNatterPublic'), 'OpenNatterPublic'),
  NatterAddToCalendar: lazyWithRetry(() => import('natters/pages/OpenNatterAddToCalendar'), 'OpenNatterAddToCalendar'),
  NataliePublic: lazyWithRetry(() => import('pages/Natalie/NataliePublic'), 'NataliePublic'),
  AccountlessNatalie: lazyWithRetry(() => import('pages/Natalie/AccountlessNatalie'), 'AccountlessNatalie'),
  Otp: lazyWithRetry(() => import('pages/Otp'), 'Otp'),
};

export const PreApp = () => {
  useIntercom();
  useInternetConnectionWatcher();

  return (
    <Sentry.ErrorBoundary fallback={<ErrorHandler />}>
      <ApiErrorAlertContainer />
      <Suspense fallback={<SuspenseFallback />}>
        <Switch>
          <SentryRoute path={AppRoutes.EventAddToCalendar} component={lazyLoadedPages.EventAddToCalendar} />
          <SentryRoute path={AppRoutes.EventPublic} component={lazyLoadedPages.EventPublic} />
          <SentryRoute path={NattersRoutes.NatterPublic} exact component={lazyLoadedPages.OpenNatterPublic} />
          <SentryRoute path={NattersRoutes.NatterAddToCalendar} exact component={lazyLoadedPages.NatterAddToCalendar} />
          <SentryRoute path={NatalieRoutes.Public} exact component={lazyLoadedPages.NataliePublic} />
          <SentryRoute
            path={NatalieRoutes.AccountlessLanding}
            render={(routeProps) => {
              const { inviteCode } = routeProps.match.params as { inviteCode: string };
              // Only match accountless flow if inviteCode looks like a real 12-char invite code
              // AND is not a registered sub-route that happens to fit the pattern (e.g.
              // "waiting-room" is 12 chars). Otherwise fall through to the authenticated App.
              if (RESERVED_NATALIE_SUBROUTES.has(inviteCode) || !INVITE_CODE_PATTERN.test(inviteCode)) {
                return <App />;
              }
              const AccountlessNatalie = lazyLoadedPages.AccountlessNatalie;
              return <AccountlessNatalie />;
            }}
          />
          <SentryRoute path={AppRoutes.PublicReport} component={lazyLoadedPages.PublicReport} />
          <SentryRoute path={AppRoutes.PublicEventReport} component={lazyLoadedPages.PublicEventReport} />
          <SentryRoute path={AppRoutes.TechCheck} component={lazyLoadedPages.TechCheck} />
          <SentryRoute path={AppRoutes.CustomLogin} component={lazyLoadedPages.CustomLogin} />
          <SentryRoute path={AppRoutes.MagicLinkCheckYourInbox} component={lazyLoadedPages.CheckYourInbox} />
          <SentryRoute path={AppRoutes.MagicLinkLinkExpired} component={lazyLoadedPages.LinkExpired} />
          <SentryRoute path={AppRoutes.MagicLinkLinkInvalid} component={lazyLoadedPages.LinkInvalid} />
          <SentryRoute path={AppRoutes.Otp} component={lazyLoadedPages.Otp} />

          <SentryRoute strict path="/" component={App} />
        </Switch>
      </Suspense>
      <AlertsContainer context="APP" />
    </Sentry.ErrorBoundary>
  );
};
